import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User, Bot, Headset, AlertCircle } from 'lucide-react';
import { auth, db } from '../firebase';
import { OptiVeloLogo } from './OptiVeloLogo';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
}

function getFallbackChatResponse(messageText: string): string {
  const norm = messageText.toLowerCase();
  
  if (norm.includes("preço") || norm.includes("plano") || norm.includes("pagar") || norm.includes("valor") || norm.includes("custo")) {
    return "Atualmente oferecemos três planos principais na OptiVelo para acelerar suas páginas:\n\n1. **Plano Pro**: R$ 197/mês (Membro Fundador) ou R$ 297/mês tradicional.\n2. **Plano Scale**: R$ 347/mês (Membro Fundador) ou R$ 497/mês tradicional.\n3. **Plano Enterprise**: R$ 647/mês (Membro Fundador) ou R$ 997/mês tradicional.\n\nTodos os planos contam com nosso período de **teste gratuito de 14 dias**! Qual deles se encaixa melhor no momento atual da sua empresa?";
  }
  
  if (norm.includes("teste") || norm.includes("grátis") || norm.includes("gratis") || norm.includes("trial") || norm.includes("experimentar")) {
    return "Com certeza! Você pode começar hoje mesmo com nosso período de **teste 100% gratuito de 14 dias** para validar as otimizações em tempo real nas suas páginas de vendas. Basta acessar a seção de assinatura em nosso painel!";
  }
  
  if (norm.includes("ajuda") || norm.includes("suporte") || norm.includes("erro") || norm.includes("problema") || norm.includes("funciona")) {
    return "Olá! Eu sou o Consultor OptiVelo. Se você estiver precisando de ajuda com sua conta, integrações ou se houver alguma questão técnica pendente, me avise para que eu possa ajudá-lo ou encaminhar para a nossa equipe de suporte dedicado para atendimento imediato!";
  }

  return "Olá! Sou o Consultor do OptiVelo. Compreendo sua dúvida! Como especialista em otimização de páginas de alto tráfego, posso te ajudar a escolher o melhor plano, ativar seu teste grátis de 14 dias ou melhorar seus anúncios de marketing. Como posso contribuir para impulsionar suas conversões hoje?";
}

export const SupportChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isHumanTransfer, setIsHumanTransfer] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
      setShowTooltip(false);
    };
    window.addEventListener('open-support-chat', handleOpenChat);
    return () => window.removeEventListener('open-support-chat', handleOpenChat);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (overrideMessage?: string) => {
    const messageText = overrideMessage || input;
    if (!messageText.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: messages.map(m => ({ role: m.role, text: m.text })),
          messageText
        })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || contentType.includes('text/html')) {
        console.warn("API returned error or HTML index.html (static routing). Running client-side chat fallback...");
        const fullText = getFallbackChatResponse(messageText);
        setMessages(prev => [...prev, {
          role: 'model',
          text: fullText,
          timestamp: new Date()
        }]);
        return;
      }

      const data = await response.json();
      const fullText = data.text || "";

      setMessages(prev => [...prev, {
        role: 'model',
        text: fullText.replace("[TRANSFER_TO_HUMAN]", "").trim(),
        timestamp: new Date()
      }]);
      
      if (fullText.includes("[TRANSFER_TO_HUMAN]")) {
        setIsHumanTransfer(true);
        
        // Log ticket to Firestore
        if (auth.currentUser) {
          await addDoc(collection(db, 'support_tickets'), {
            userId: auth.currentUser.uid,
            userEmail: auth.currentUser.email,
            history: [...messages, userMessage].map(m => ({ role: m.role, text: m.text })),
            status: 'pending',
            createdAt: serverTimestamp()
          });
        }
      }
    } catch (error: any) {
      console.warn("VeloBot Neural Engine Error, launching local assistant:", error);
      const fullText = getFallbackChatResponse(messageText);
      setMessages(prev => [...prev, {
        role: 'model',
        text: fullText,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {showTooltip && (
          <div className="bg-white text-slate-900 p-3 rounded-xl shadow-2xl max-w-[160px] animate-in fade-in slide-in-from-bottom-2 relative group border border-indigo-100">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(false);
              }}
              className="absolute -top-2 -left-2 w-5 h-5 bg-white border border-indigo-100 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-500 hover:scale-110 transition-all shadow-sm opacity-0 group-hover:opacity-100"
            >
              <X className="w-3 h-3" />
            </button>
            <p className="text-[12px] font-bold leading-tight">
              Dúvidas? Posso te ajudar! 🚀
            </p>
            <div className="absolute bottom-[-6px] right-6 w-3 h-3 bg-white border-r border-b border-indigo-100 rotate-45"></div>
          </div>
        )}
        <button 
          onClick={() => {
            setIsOpen(true);
            setShowTooltip(false);
          }}
          className="w-12 h-12 bg-indigo-500 text-white rounded-full shadow-2xl shadow-indigo-500/40 flex items-center justify-center hover:scale-110 transition-all group relative"
        >
          <MessageCircle className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-black rounded-full"></div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-[#0b0e14] border border-white/10 rounded-3xl shadow-2xl flex flex-col z-50 animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="p-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02] rounded-t-3xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center text-white shadow-lg shadow-indigo-500/10">
            <OptiVeloLogo size={24} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">VeloBot</h3>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-[12px] text-slate-500 font-bold uppercase tracking-widest">Quantum Intelligence Active</span>
            </div>
          </div>
        </div>
        <button 
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide"
      >
        {messages.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <div className="w-12 h-12 bg-indigo-500/5 rounded-full flex items-center justify-center mx-auto text-indigo-500/40">
              <Headset className="w-6 h-6" />
            </div>
            <div className="space-y-2">
              <p className="text-xs text-slate-500 font-medium px-8">
                Olá! Eu sou o VeloBot. Como posso ajudar você a escalar suas conversões hoje?
              </p>
              <div className="flex flex-wrap justify-center gap-2 px-4 pt-2">
                {['Como funciona?', 'Quais os preços?', 'Tem teste grátis?'].map((q) => (
                  <button 
                    key={q}
                    onClick={() => handleSend(q)}
                    className="text-[12px] font-bold uppercase tracking-wider px-3 py-2 bg-white/5 hover:bg-indigo-500/20 border border-white/10 rounded-lg text-slate-300 transition-all"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
              m.role === 'user' 
                ? 'bg-indigo-500 text-white rounded-tr-none' 
                : 'bg-white/5 text-slate-300 rounded-tl-none border border-white/5'
            }`}>
              <div className="markdown-body">
                <ReactMarkdown>{m.text}</ReactMarkdown>
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/5 p-3 rounded-2xl rounded-tl-none border border-white/5 flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
            </div>
          </div>
        )}

        {isHumanTransfer && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl space-y-3">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">Aviso de Atendimento</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Nossa equipe humana está com alta demanda no momento. Seu ticket foi gerado e entraremos em contato via e-mail o mais breve possível.
            </p>
            <button 
              onClick={() => setIsHumanTransfer(false)}
              className="w-full py-2 bg-white/5 hover:bg-white/10 text-white text-[12px] font-bold uppercase tracking-widest rounded-xl transition-all"
            >
              Continuar com VeloBot
            </button>
          </div>
        )}
      </div>

      {/* Input */}
      {!isHumanTransfer && (
        <div className="p-4 border-t border-white/5">
          <div className="relative">
            <input 
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Digite sua dúvida..."
              className="w-full bg-white/[0.03] border border-white/10 rounded-2xl pl-4 pr-12 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all"
            />
            <button 
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-400 transition-all disabled:opacity-50 disabled:hover:bg-indigo-500"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-slate-600 text-center mt-3 uppercase tracking-widest font-bold">
            Quantum Neural Engine v3.0
          </p>
        </div>
      )}
    </div>
  );
};
